import { useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { ProtectedRoute } from '../components/auth/index.js';
import {
  BoardCards,
  CreateBoardForm,
  LoadState,
  MyActionsList,
} from '../components/features/org-issues/index.js';
import { useMyOrgActions, useOrgBoards } from '../hooks/useOrgIssues.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題のトップ。自分が見られるボードの一覧と、ボード横断で自分が担当の未完了 Next（期日の近い順）を並べ、ボードを作る入口も置く。MCP（org-issues）と同じ API を叩く
 * @graph-connects useOrgBoards [calls] 見られるボードの一覧
 * @graph-connects useMyOrgActions [calls] 自分の未完了 Next
 * @graph-connects BoardCards [calls] ボードの一覧
 * @graph-connects MyActionsList [calls] 自分の未完了 Next の一覧
 * @graph-connects CreateBoardForm [calls] ボードの作成
 */
export const Route = createFileRoute('/org-issues/')({
  component: OrgIssuesRoute,
});

/** @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする */
function OrgIssuesRoute(): ReactNode {
  return (
    <ProtectedRoute pageKey="orgIssues">
      <OrgIssuesPage />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function OrgIssuesPage(): ReactNode {
  const { t } = useTranslation();
  const boards = useOrgBoards();
  const actions = useMyOrgActions();
  const [composing, setComposing] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
            {t('orgIssues.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t('orgIssues.intro')}</p>
        </div>
        {!composing && (
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            onClick={() => {
              setComposing(true);
            }}
          >
            {t('orgIssues.createBoard.open')}
          </Button>
        )}
      </div>

      {composing && (
        <CreateBoardForm
          onCancel={() => {
            setComposing(false);
          }}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('orgIssues.boards.heading')}
          </h2>
          <LoadState
            isLoading={boards.isLoading}
            isError={boards.isError}
            onRetry={() => {
              void boards.refetch();
            }}
          />
          {boards.data !== undefined && <BoardCards boards={boards.data.boards} />}
        </section>
        <section className="space-y-2">
          <LoadState
            isLoading={actions.isLoading}
            isError={actions.isError}
            onRetry={() => {
              void actions.refetch();
            }}
          />
          {actions.data !== undefined && <MyActionsList actions={actions.data.actions} />}
        </section>
      </div>
    </div>
  );
}
